import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import type { LoginResponse } from '../api/auth'
import { fetchOperators, type Operator } from '../api/catalog'
import {
  RECEIVABLE_STATUSES, RECEIVABLE_STATUS_LABELS, receivablesApi,
  type ReceivableRow, type ReceivableStatus, type ReceivableSummary,
} from '../api/receivables'
import { RevenueRulesPanel } from '../components/RevenueRulesPanel'
import { useFeatureFlag } from '../lib/featureFlags'
import { dateIt, eur, errMsg, num, useOperatorNames } from '../lib/financeFormat'
import {
  BarChart, Button, Callout, CardGrid, CardGridItem, ChartCard, DataTable, DataTableEmptyRow, DateField, DonutChart, Dialog,
  EmptyState, FilterBar, FormGrid, LoadingState, OverflowMenu, Page, PageHeader, Pagination, Select, StatCard,
  StatGrid, StatusPill, Tabs, TextField, useSnackbar, usePrompt, type PillTone,
} from '../components/ui'

/**
 * Blocco 1 / Card D (REQ-125-001, 134-001, 136-001, 168-001, 200-001) — crediti attesi: elenco, aging per fascia, stato,
 * azioni (contesta, risolvi, annulla, modifica data/probabilita') e regole di maturazione. Nessun calcolo: tutto arriva
 * dal server, l'incassato e' derivato dalle allocazioni riconciliate e non e' mai modificabile da qui. Dietro il flag
 * `finanza.crediti_attesi`. Il gating reale e' server-side (FinanceAdministrationRead / FinanceAdministration).
 */

interface Props {
  auth: LoginResponse
}

type Tab = 'list' | 'rules'

const PAGE_SIZES = [25, 50, 100] as const

const STATUS_TONE: Record<ReceivableStatus, PillTone> = {
  Previsto: 'neutral',
  Maturato: 'primary',
  DaIncassare: 'info',
  ParzialmenteIncassato: 'info',
  Incassato: 'success',
  Contestato: 'warning',
  Scaduto: 'error',
  Stornato: 'neutral',
  Annullato: 'neutral',
}

const CLOSED: ReceivableStatus[] = ['Incassato', 'Stornato', 'Annullato']

interface Filters {
  status: string
  operatorId: string
  from: string
  to: string
}

const EMPTY_FILTERS: Filters = { status: '', operatorId: '', from: '', to: '' }

export function ReceivablesPage({ auth }: Props) {
  const writer = auth.role === 'Administrator' || auth.role === 'EqualAdmin' || auth.role === 'EqualAmministrazione'
  const enabled = useFeatureFlag('finanza.crediti_attesi')
  const [tab, setTab] = useState<Tab>('list')

  return (
    <Page>
      <PageHeader title="Crediti attesi" icon="price_change" />
      {!enabled ? (
        <EmptyState icon="toggle_off" title="Funzione non attiva" description="I crediti attesi non sono ancora attivi per il tuo utente." />
      ) : (
        <>
          {!writer && <Callout tone="info">Sola lettura: le modifiche richiedono il ruolo Amministrazione.</Callout>}
          <Tabs
            aria-label="Sezioni dei crediti attesi"
            value={tab}
            onChange={id => setTab(id as Tab)}
            items={[{ id: 'list', label: 'Crediti' }, { id: 'rules', label: 'Regole di maturazione' }]}
          />
          {tab === 'list' && <ListTab writer={writer} />}
          {tab === 'rules' && <RevenueRulesPanel writer={writer} lifecycle={auth.role === 'Administrator' || auth.role === 'EqualAdmin'} />}
        </>
      )}
    </Page>
  )
}

function ListTab({ writer }: { writer: boolean }) {
  const snackbar = useSnackbar()
  const prompt = usePrompt()
  const operatorNames = useOperatorNames()
  const [operators, setOperators] = useState<Operator[]>([])
  const [form, setForm] = useState<Filters>(EMPTY_FILTERS)
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(25)
  const [rows, setRows] = useState<ReceivableRow[]>([])
  const [total, setTotal] = useState(0)
  const [sample, setSample] = useState<ReceivableRow[]>([])
  const [sampleTotal, setSampleTotal] = useState(0)
  const [summary, setSummary] = useState<ReceivableSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [syncing, setSyncing] = useState(false)
  const [editing, setEditing] = useState<ReceivableRow | null>(null)

  useEffect(() => {
    let alive = true
    void fetchOperators().then(r => { if (alive) setOperators(r.items) }).catch(() => undefined)
    return () => { alive = false }
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    const filt = { status: applied.status, operatorId: applied.operatorId, from: applied.from, to: applied.to }
    try {
      const [list, sum, wide] = await Promise.all([
        receivablesApi.list({ ...filt, page, pageSize }),
        receivablesApi.summary(filt),
        receivablesApi.list({ ...filt, page: 1, pageSize: 200 }),
      ])
      setRows(list.items)
      setTotal(list.total)
      setSummary(sum)
      setSample(wide.items)
      setSampleTotal(wide.total)
    } catch (e) {
      setError(errMsg(e, 'Impossibile caricare i crediti.'))
    } finally {
      setLoading(false)
    }
  }, [applied, page, pageSize])

  useEffect(() => { void load() }, [load])

  const opName = (id: number | null) => (id === null ? '—' : operatorNames.get(id) ?? `Operatore ${id}`)

  async function sync() {
    setSyncing(true)
    try {
      const r = await receivablesApi.sync()
      snackbar.show({ message: `Sincronizzazione: ${r.created} creati, ${r.updated} aggiornati, ${r.unchanged} invariati, ${r.skipped} saltati.`, tone: 'success' })
      await load()
    } catch (e) {
      snackbar.show({ message: errMsg(e, 'Sincronizzazione non riuscita.'), tone: 'error' })
    } finally {
      setSyncing(false)
    }
  }

  async function act(fn: () => Promise<unknown>, ok: string) {
    try {
      await fn()
      snackbar.show({ message: ok, tone: 'success' })
      await load()
    } catch (e) {
      snackbar.show({ message: errMsg(e, 'Operazione non riuscita.'), tone: 'error' })
    }
  }

  async function dispute(r: ReceivableRow) {
    const reason = await prompt({ title: 'Contesta il credito', label: 'Motivo della contestazione', required: true, multiline: true, confirmLabel: 'Contesta' })
    if (reason) await act(() => receivablesApi.dispute(r.id, reason), 'Credito contestato.')
  }

  async function cancel(r: ReceivableRow) {
    const reason = await prompt({ title: 'Annulla il credito', label: 'Motivo dell’annullamento', required: true, multiline: true, confirmLabel: 'Annulla credito', destructive: true })
    if (reason) await act(() => receivablesApi.cancel(r.id, reason), 'Credito annullato.')
  }

  const aging = summary?.aging
  const amountsHidden = summary !== null && summary.total === undefined
  const donutByAmount = sample.some(r => r.residualAmount !== undefined)
  const slices = RECEIVABLE_STATUSES.map(s => {
    const mine = sample.filter(r => r.status === s)
    return { id: s, label: RECEIVABLE_STATUS_LABELS[s], value: donutByAmount ? mine.reduce((a, r) => a + (r.residualAmount ?? 0), 0) : mine.length }
  })

  return (
    <>
      <FilterBar
        activeCount={Object.values(applied).filter(Boolean).length}
        busy={loading}
        onSubmit={() => { setPage(1); setApplied(form) }}
        onReset={() => { setForm(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); setPage(1) }}
        actions={writer ? <Button variant="tonal" icon="sync" loading={syncing} onClick={() => void sync()}>Sincronizza</Button> : undefined}
      >
        <Select label="Stato" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
          <option value="">Tutti</option>
          {RECEIVABLE_STATUSES.map(s => <option key={s} value={s}>{RECEIVABLE_STATUS_LABELS[s]}</option>)}
        </Select>
        <Select label="Operatore" value={form.operatorId} onChange={e => setForm({ ...form, operatorId: e.target.value })}>
          <option value="">Tutti</option>
          {operators.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </Select>
        <DateField kind="date" label="Incasso previsto dal" value={form.from} onChange={e => setForm({ ...form, from: e.target.value })} />
        <DateField kind="date" label="Incasso previsto al" value={form.to} onChange={e => setForm({ ...form, to: e.target.value })} />
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}

      <StatGrid>
        <StatCard label="Residuo da incassare" value={eur(summary?.total)} />
        <StatCard label="Scaduto" value={eur(summary?.overdue)} tone={summary?.overdue ? 'error' : 'neutral'} />
        <StatCard label="In scadenza a 30 giorni" value={eur(summary?.dueSoon30)} />
        <StatCard label="Contestato" value={eur(summary?.disputed)} tone={summary?.disputed ? 'warning' : 'neutral'} />
        <StatCard label="Incassato nel mese" value={eur(summary?.collectedMonth)} tone="success" />
      </StatGrid>

      {amountsHidden && !loading && <Callout tone="info">Gli importi non sono visibili con il tuo profilo: restano disponibili stati e scadenze.</Callout>}

      <CardGrid min={360}>
        <CardGridItem>
          <ChartCard title="Aging dei crediti" subtitle="Residuo per giorni di ritardo">
            <BarChart
              label="Aging dei crediti"
              categoryHeading="Fascia"
              unit="eur"
              loading={loading}
              categories={['0-30 giorni', '31-60 giorni', '61-90 giorni', 'Oltre 90 giorni']}
              series={[{ id: 'residuo', name: 'Residuo', color: 'primary', values: aging ? [aging.d0_30, aging.d31_60, aging.d61_90, aging.d90p] : [] }]}
              emptyTitle="Nessun dato di aging"
              emptyDescription={amountsHidden ? 'Gli importi non sono visibili con il tuo profilo.' : 'Non ci sono crediti scaduti.'}
            />
          </ChartCard>
        </CardGridItem>
        <CardGridItem>
          <ChartCard
            title="Crediti per stato"
            subtitle={sampleTotal > sample.length ? `Primi ${sample.length} di ${num(sampleTotal)} crediti` : donutByAmount ? 'Residuo per stato' : 'Numero di crediti per stato'}
          >
            <DonutChart
              label="Crediti per stato"
              unit={donutByAmount ? 'eur' : 'number'}
              totalLabel={donutByAmount ? 'Residuo' : 'Crediti'}
              loading={loading}
              slices={slices}
              emptyTitle="Nessun credito"
            />
          </ChartCard>
        </CardGridItem>
      </CardGrid>

      {loading && rows.length === 0 ? <LoadingState /> : (
        <DataTable
          aria-label="Crediti attesi"
          footer={<Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} pageSizeOptions={PAGE_SIZES} onPageSizeChange={s => { setPageSize(s); setPage(1) }} />}
        >
          <thead>
            <tr>
              <th>Pratica</th><th>Operatore</th><th>Stato</th><th className="text-end">Prob.</th>
              <th className="text-end">Atteso</th><th className="text-end">Maturato</th><th className="text-end">Incassato</th><th className="text-end">Residuo</th>
              <th>Incasso previsto</th><th className="text-end">Ritardo</th>{writer && <th><span className="md-sr-only">Azioni</span></th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <DataTableEmptyRow colSpan={writer ? 11 : 10} title="Nessun credito" description="Nessun credito corrisponde ai filtri scelti." />}
            {rows.map(r => (
              <tr key={r.id}>
                <td><Link to={r.drill?.href ?? `/contracts/${r.contractId}`}>Pratica {r.contractId}</Link></td>
                <td>{opName(r.operatorId)}</td>
                <td><StatusPill tone={STATUS_TONE[r.status]}>{RECEIVABLE_STATUS_LABELS[r.status]}</StatusPill></td>
                <td className="text-end md-num">{r.probability}%</td>
                <td className="text-end md-num">{eur(r.expectedAmount)}</td>
                <td className="text-end md-num">{eur(r.accruedAmount)}</td>
                <td className="text-end md-num">{eur(r.collectedAmount)}</td>
                <td className="text-end md-num">{eur(r.residualAmount)}</td>
                <td>{dateIt(r.expectedCollectionDate)}</td>
                <td className="text-end md-num">{r.daysOverdue > 0 ? `${r.daysOverdue} gg` : '—'}</td>
                {writer && (
                  <td>
                    {CLOSED.includes(r.status) ? null : (
                      <OverflowMenu
                        aria-label={`Azioni sul credito della pratica ${r.contractId}`}
                        size="sm"
                        items={[
                          { label: 'Modifica data e probabilità', icon: 'edit', onSelect: () => setEditing(r) },
                          r.status === 'Contestato'
                            ? { label: 'Risolvi contestazione', icon: 'task_alt', onSelect: () => void act(() => receivablesApi.resolve(r.id), 'Contestazione risolta.') }
                            : { label: 'Contesta', icon: 'report', onSelect: () => void dispute(r) },
                          { label: 'Annulla credito', icon: 'cancel', onSelect: () => void cancel(r) },
                        ]}
                      />
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}

      {summary?.byOperator && summary.byOperator.length > 0 && (
        <DataTable aria-label="Crediti per operatore" caption="Crediti per operatore">
          <thead>
            <tr><th>Operatore</th><th className="text-end">Residuo</th><th className="text-end">Scaduto</th><th className="text-end">Ritardo medio</th></tr>
          </thead>
          <tbody>
            {summary.byOperator.map(o => (
              <tr key={o.operatorId}>
                <td>{opName(o.operatorId)}</td>
                <td className="text-end md-num">{eur(o.residual)}</td>
                <td className="text-end md-num">{eur(o.overdue)}</td>
                <td className="text-end md-num">{o.avgDelayDays === null ? '—' : `${num(o.avgDelayDays, 1)} gg`}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}

      {editing && <EditDialog receivable={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load() }} />}
    </>
  )
}

function EditDialog({ receivable, onClose, onSaved }: { receivable: ReceivableRow; onClose: () => void; onSaved: () => void }) {
  const snackbar = useSnackbar()
  const [date, setDate] = useState(receivable.expectedCollectionDate?.slice(0, 10) ?? '')
  const [probability, setProbability] = useState(String(receivable.probability))
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const p = Number(probability)
  const invalid = probability === '' || !Number.isInteger(p) || p < 0 || p > 100

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (invalid) return
    setBusy(true)
    try {
      await receivablesApi.patch(receivable.id, { expectedCollectionDate: date || null, probability: p, ...(notes.trim() ? { notes: notes.trim() } : {}) })
      snackbar.show({ message: 'Credito aggiornato.', tone: 'success' })
      onSaved()
    } catch (err) {
      snackbar.show({ message: errMsg(err, 'Aggiornamento non riuscito.'), tone: 'error' })
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      as="form"
      onSubmit={e => void submit(e)}
      title={`Credito della pratica ${receivable.contractId}`}
      supportingText="L'incassato non si modifica da qui: arriva dalle riconciliazioni bancarie."
      actions={
        <>
          <Button variant="text" dismiss onClick={onClose}>Annulla</Button>
          <Button variant="filled" type="submit" loading={busy} disabled={invalid}>Salva</Button>
        </>
      }
    >
      <FormGrid>
        <DateField kind="date" label="Incasso previsto" value={date} onChange={e => setDate(e.target.value)} />
        <TextField
          label="Probabilità"
          type="number"
          min={0}
          max={100}
          suffix="%"
          value={probability}
          error={invalid ? 'Da 0 a 100.' : undefined}
          onChange={e => setProbability(e.target.value)}
        />
        <TextField className="md-span-full" label="Note" multiline value={notes} onChange={e => setNotes(e.target.value)} />
      </FormGrid>
    </Dialog>
  )
}

